/** Ad/channel type display labels, keyed by advType string. */
export const ADV_TYPE_LABELS: Record<string, string> = {
  'pre-roll': 'YT Pre-roll',
  full: 'YT Full',
  short: 'YT Shorts',
  integration: 'YT Integration',
  'stream-highlight': 'YT Stream Highlight',
  'youtube-community-post': 'YT Community Post',
  stream: 'Stream',
  vod: 'Twitch VOD',
  tweet: 'Tweet',
  tiktok: 'TikTok',
  'tiktok-live': 'TikTok Live',
  douyin: 'Douyin',
  'instagram-post': 'Instagram Post',
  'instagram-reel': 'Instagram Reel',
  'instagram-story': 'Instagram Story',
  'facebook-post': 'Facebook Post',
  'facebook-reel': 'Facebook Reel',
  'discord-post': 'Discord Post',
  'footage-right': 'Footage Right',
  'chzzk-video': 'Chzzk Video',
  'afreeca-video': 'Afreeca Video',
  'bilibili-video': 'Bilibili Video',
  'sm-post': 'SM Post',
  'item-reimbursement': 'Item Reimb.',
};

export const ADV_TYPE_OPTIONS = Object.entries(ADV_TYPE_LABELS);

/**
 * Per-channel ordered advType preference used when adding a new rate row,
 * keyed by channel (platform) type. The first entry is the default selection;
 * subsequent entries are tried in order, skipping types already present.
 * Extends the original DEFAULT_ADV_TYPE_BY_CHANNEL_TYPE mapping with
 * stream-based platforms (chzzk/afreeca/bilibili) and facebook.
 */
export const ADV_TYPE_ORDER_BY_CHANNEL_TYPE: Record<string, string[]> = {
  youtube: ['pre-roll', 'full', 'short', 'integration', 'stream-highlight', 'youtube-community-post'],
  twitch: ['stream', 'vod'],
  chzzk: ['stream', 'chzzk-video'],
  afreeca: ['stream', 'afreeca-video'],
  bilibili: ['stream', 'bilibili-video'],
  tiktok: ['tiktok', 'tiktok-live'],
  // Instagram Reel is the default pick; Post and Story follow.
  instagram: ['instagram-reel', 'instagram-post', 'instagram-story'],
  douyin: ['douyin'],
  twitter: ['tweet'],
  facebook: ['facebook-post', 'facebook-reel'],
  discord: ['discord-post'],
};

/** Fallback advType for channel types without an explicit order. */
export const DEFAULT_ADV_TYPE = 'pre-roll';

/**
 * Ordered advType preference for a channel: the platform-specific order first,
 * then any remaining global types as a fallback so Add rate always resolves a
 * next type.
 */
export function getAdvTypePriority(channelType?: string | null): string[] {
  const order = (channelType && ADV_TYPE_ORDER_BY_CHANNEL_TYPE[channelType]) || [DEFAULT_ADV_TYPE];
  const rest = ADV_TYPE_OPTIONS.map(([k]) => k).filter((k) => !order.includes(k));
  return [...order, ...rest];
}

export interface VariationConfig {
  name: string;
  options: Array<{ value: number | string; text: string }>;
  default: number | string;
}

/** Ad types that carry a variation field (hours / duration). */
export const ADV_TYPES_WITH_VARIATION: Record<string, VariationConfig> = {
  stream: {
    name: 'hours',
    options: [1, 2, 3, 4].map((h) => ({ value: h, text: `${h}h` })),
    default: 2,
  },
  'footage-right': {
    name: 'duration',
    options: [
      { value: '1month', text: '1 month' },
      { value: '3months', text: '3 months' },
      { value: '6months', text: '6 months' },
      { value: '1year', text: '1 year' },
    ],
    default: '3months',
  },
  'tiktok-live': {
    name: 'hours',
    options: [1, 2, 3].map((h) => ({ value: h, text: `${h}h` })),
    default: 1,
  },
};

export interface RateEntry {
  type: string;
  price: string;
  variation?: number | string | null;
  updatedAt?: string | null;
}

/**
 * One-sentence explainer per advType, for the "Ad Type Guide" popover next to
 * "Add rate" (channel Rates section). No BE/legacy source documents these in enough
 * detail to reuse verbatim (BE's ExternalAPISpec.md and gc-mono's RatesTab only carry
 * the same short labels as ADV_TYPE_LABELS) — written fresh here, one entry per key.
 */
export const ADV_TYPE_DESCRIPTIONS: Record<string, { en: string; ko: string }> = {
  'pre-roll': {
    en: 'A short sponsored clip shown before the main content of a new or existing YouTube video.',
    ko: '신규 또는 기존 유튜브 영상 시작 전에 삽입되는 짧은 광고 구간입니다.',
  },
  full: {
    en: "A full YouTube video entirely dedicated to the sponsor's game or product.",
    ko: '영상 전체가 스폰서의 게임·제품에 할애되는 유튜브 단독(Dedicated) 영상입니다.',
  },
  short: {
    en: 'A YouTube Shorts video featuring the sponsored content.',
    ko: '스폰서 콘텐츠를 담은 유튜브 쇼츠(세로형 숏폼) 영상입니다.',
  },
  integration: {
    en: 'The sponsored content is naturally worked into part of a regular-length video, rather than taking up the whole video.',
    ko: '영상 전체가 아니라 일반 영상의 일부 구간에 스폰서 콘텐츠를 자연스럽게 녹여 넣는 방식입니다.',
  },
  'stream-highlight': {
    en: 'A highlight video edited down from a live stream that featured the sponsored content.',
    ko: '스폰서 콘텐츠가 포함된 라이브 스트림을 편집한 하이라이트 영상입니다.',
  },
  'youtube-community-post': {
    en: "A post published to the channel's YouTube Community tab, not a video.",
    ko: '영상이 아닌 유튜브 채널의 커뮤니티 탭에 게시되는 포스트입니다.',
  },
  stream: {
    en: 'A live stream featuring the sponsored content for an agreed duration. Shared across Twitch, CHZZK, SOOP, and Bilibili — the actual platform follows the channel itself.',
    ko: '합의된 시간 동안 스폰서 콘텐츠를 포함해 진행하는 라이브 스트림입니다. Twitch·CHZZK·SOOP·Bilibili 등 스트리밍 플랫폼 공통으로 쓰이며, 실제 플랫폼은 채널 자체의 유형을 따릅니다.',
  },
  vod: {
    en: 'A recorded video-on-demand upload of a Twitch stream that featured the sponsored content.',
    ko: '스폰서 콘텐츠가 포함된 트위치 스트림을 다시보기(VOD)로 업로드한 영상입니다.',
  },
  tweet: {
    en: 'A sponsored post on X (Twitter).',
    ko: 'X(트위터)에 게시하는 스폰서 포스트입니다.',
  },
  tiktok: {
    en: 'A short-form sponsored video on TikTok.',
    ko: '틱톡에 게시하는 숏폼 스폰서 영상입니다.',
  },
  'tiktok-live': {
    en: 'A TikTok live stream featuring the sponsored content for an agreed duration.',
    ko: '합의된 시간 동안 스폰서 콘텐츠를 포함해 진행하는 틱톡 라이브 스트림입니다.',
  },
  douyin: {
    en: 'A short-form sponsored video on Douyin (the China-only counterpart of TikTok).',
    ko: '더우인(중국 내수용 틱톡)에 게시하는 숏폼 스폰서 영상입니다.',
  },
  'instagram-post': {
    en: 'A sponsored feed post on Instagram.',
    ko: '인스타그램 피드에 게시하는 스폰서 포스트입니다.',
  },
  'instagram-reel': {
    en: 'A short-form sponsored video on Instagram (Reels).',
    ko: '인스타그램 릴스(숏폼 영상)로 게시하는 스폰서 콘텐츠입니다.',
  },
  'instagram-story': {
    en: 'A sponsored Instagram Story (visible for 24 hours).',
    ko: '24시간 동안 노출되는 인스타그램 스토리 스폰서 콘텐츠입니다.',
  },
  'facebook-post': {
    en: 'A sponsored feed post on Facebook.',
    ko: '페이스북 피드에 게시하는 스폰서 포스트입니다.',
  },
  'facebook-reel': {
    en: 'A short-form sponsored video on Facebook (Reels).',
    ko: '페이스북 릴스(숏폼 영상)로 게시하는 스폰서 콘텐츠입니다.',
  },
  'discord-post': {
    en: "A sponsored announcement posted in the influencer's Discord server.",
    ko: '인플루언서의 디스코드 서버에 게시하는 스폰서 공지입니다.',
  },
  'chzzk-video': {
    en: 'An uploaded video (VOD/clip) on CHZZK featuring the sponsored content.',
    ko: '스폰서 콘텐츠가 포함된 치지직(CHZZK) 업로드 영상(다시보기·클립)입니다.',
  },
  'afreeca-video': {
    en: 'An uploaded video (VOD/clip) on SOOP featuring the sponsored content.',
    ko: '스폰서 콘텐츠가 포함된 숲(SOOP) 업로드 영상(다시보기·클립)입니다.',
  },
  'bilibili-video': {
    en: 'A sponsored video uploaded to Bilibili.',
    ko: '빌리빌리(Bilibili)에 업로드하는 스폰서 영상입니다.',
  },
  'footage-right': {
    en: "Licensing rights to reuse the influencer's produced footage for a set period, separate from the content deliverable itself.",
    ko: '인플루언서가 제작한 영상 소스를 일정 기간 재사용할 수 있는 라이선스로, 콘텐츠 결과물 자체와는 별개입니다.',
  },
  'sm-post': {
    en: "A general social-media post that doesn't map to a specific platform type above.",
    ko: '위 항목에 해당하지 않는 일반적인 소셜 미디어 포스트입니다.',
  },
  'item-reimbursement': {
    en: 'Reimbursement for an in-game item or purchase made as part of the partnership, rather than a content deliverable.',
    ko: '콘텐츠 결과물이 아니라 파트너십의 일환으로 발생한 인게임 아이템·구매 비용을 정산하는 항목입니다.',
  },
};

export interface AdvTypeGuideGroup {
  label: { en: string; ko: string };
  types: string[];
}

/**
 * Groups every `ADV_TYPE_LABELS` key exactly once, for the "Ad Type Guide" popover.
 * Distinct from `ADV_TYPE_ORDER_BY_CHANNEL_TYPE` (a per-channel "next type to add"
 * priority list, which deliberately repeats the shared `stream` type across every
 * stream-based platform) — here `stream` gets its own single group instead.
 */
export const ADV_TYPE_GUIDE_GROUPS: AdvTypeGuideGroup[] = [
  { label: { en: 'YouTube', ko: 'YouTube' }, types: ['pre-roll', 'full', 'short', 'integration', 'stream-highlight', 'youtube-community-post'] },
  { label: { en: 'Live streaming (shared)', ko: '라이브 스트리밍 (공용)' }, types: ['stream'] },
  { label: { en: 'Twitch', ko: 'Twitch' }, types: ['vod'] },
  { label: { en: 'CHZZK', ko: 'CHZZK' }, types: ['chzzk-video'] },
  { label: { en: 'SOOP', ko: 'SOOP' }, types: ['afreeca-video'] },
  { label: { en: 'Bilibili', ko: 'Bilibili' }, types: ['bilibili-video'] },
  { label: { en: 'TikTok', ko: 'TikTok' }, types: ['tiktok', 'tiktok-live'] },
  { label: { en: 'Instagram', ko: 'Instagram' }, types: ['instagram-reel', 'instagram-post', 'instagram-story'] },
  { label: { en: 'Facebook', ko: 'Facebook' }, types: ['facebook-post', 'facebook-reel'] },
  { label: { en: 'Douyin', ko: 'Douyin' }, types: ['douyin'] },
  { label: { en: 'X (Twitter)', ko: 'X (트위터)' }, types: ['tweet'] },
  { label: { en: 'Discord', ko: 'Discord' }, types: ['discord-post'] },
  { label: { en: 'Common / Other', ko: '공용 / 기타' }, types: ['footage-right', 'sm-post', 'item-reimbursement'] },
];
